import React, { useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import styled from "styled-components";
import { toast } from "react-toastify";
import { useAppSelector } from "../../store/hooks";
import { checkRole } from "../recipe_page/recipe_page";
import BasicButton from "../basics/BasicButton";
import { useConfirm } from "../../ui/components/ConfirmDialog";
import {
  useGetAgentToolsQuery,
  usePreviewAgentEventMutation,
  useFireAgentEventMutation,
  type McpToolInfo,
  type IngestExternalPayloadPreview,
} from "../../services/rtkApi/agentInspectorAdminApi";
import { useGetEventDispatchConfigQuery } from "../../services/rtkApi/eventDispatchAdminApi";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #dff1ff;
  padding: 20px;
  min-height: 100vh;
  max-width: 1100px;
  margin: 0 auto;
  box-sizing: border-box;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 16px 20px;
  margin-top: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
`;

const Scroll = styled.div`
  overflow-x: auto;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  th,
  td {
    text-align: left;
    padding: 8px 10px;
    border-bottom: 1px solid #eee;
    vertical-align: top;
  }
  th {
    font-weight: 600;
    color: #333;
    position: sticky;
    top: 0;
    background: #fafafa;
    z-index: 1;
  }
`;

const Hint = styled.p`
  font-size: 0.85rem;
  color: #555;
  margin: 0 0 12px;
  line-height: 1.4;
`;

const Pre = styled.pre`
  background: #0f172a;
  color: #e2e8f0;
  padding: 12px;
  border-radius: 8px;
  font-size: 0.78rem;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 8px 0 0;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #333;
  input,
  select,
  textarea {
    font-weight: 400;
    font-size: 0.85rem;
    padding: 6px 8px;
    border: 1px solid #ccc;
    border-radius: 6px;
    font-family: inherit;
  }
  textarea {
    font-family: ui-monospace, monospace;
  }
`;

function badge(text: string, bg: string, color: string): React.ReactElement {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 6,
        fontWeight: 600,
        fontSize: "0.72rem",
        background: bg,
        color,
      }}
    >
      {text}
    </span>
  );
}

// renders a JSON-schema `inputSchema` as a compact parameter list
const SchemaParams: React.FC<{ schema: Record<string, unknown> }> = ({ schema }) => {
  const props = (schema?.properties as Record<string, { type?: string; description?: string }>) || {};
  const required = (schema?.required as string[]) || [];
  const keys = Object.keys(props);
  if (!keys.length) {
    return <span style={{ color: "#999", fontSize: "0.78rem" }}>no parameters</span>;
  }
  return (
    <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
      {keys.map((k) => {
        const p = props[k] || {};
        return (
          <li key={k} style={{ fontSize: "0.78rem", marginBottom: 2 }}>
            <code>{k}</code>{" "}
            <em style={{ color: "#777" }}>
              ({p.type ?? "any"}
              {required.includes(k) ? ", required" : ""})
            </em>
            {p.description ? <span style={{ color: "#555" }}> — {p.description}</span> : null}
          </li>
        );
      })}
    </ul>
  );
};

// example INPUT fixtures (editable) for the handled event types — these only exercise the
// backend builder; they are NOT the output. The constructed payload always comes from the
// backend's live buildAgentMessage(), shown in the preview below.
const EXAMPLE_METADATA: Record<string, Record<string, unknown>> = {
  "coffee.rate": { coffeeType: "espresso", coffeeShop: "Blue Bottle", overallRating: 9, description: "rich + smooth", city: "Athens", country: "Greece" },
  "aotd.create": {},
  "comment.post": { commentText: "love this one" },
  "comment.author_liked": { commentAuthorUsername: "alex" },
  "badge.earned": { badgeName: "Early Bird", badgeCode: "early_bird" },
  "recipe.make": { title: "Cold brew #3" },
  "user.join": {},
  "store.order": { items: 2 },
};

const AgentInspectorAdmin: React.FC = () => {
  const user = useAppSelector((state) => state.auth);
  const isAdmin = checkRole(user.role ?? "", "admin");
  const ready = user.bootstrapStatus === "ready";
  const confirm = useConfirm();

  const toolsQ = useGetAgentToolsQuery(undefined, { skip: !isAdmin || !ready });
  const dispatchQ = useGetEventDispatchConfigQuery(undefined, { skip: !isAdmin || !ready });

  const [preview, { isLoading: previewing }] = usePreviewAgentEventMutation();
  const [fire, { isLoading: firing }] = useFireAgentEventMutation();

  // tester state
  const [selectedType, setSelectedType] = useState<string>("coffee.rate");
  const [actorUsername, setActorUsername] = useState<string>("tester");
  const [entityId, setEntityId] = useState<string>("");
  const [metaText, setMetaText] = useState<string>(
    JSON.stringify(EXAMPLE_METADATA["coffee.rate"], null, 2),
  );
  const [result, setResult] = useState<
    { handled: boolean; payload: IngestExternalPayloadPreview | null } | null
  >(null);

  const dispatch = dispatchQ.data?.success ? dispatchQ.data.data : null;
  const effective = dispatch?.effective ?? {};
  const eventTypes = dispatch?.eventTypes ?? [];
  const routedToAgent = (t: string): boolean => (effective[t] ?? []).includes("agent");

  const platformTools = toolsQ.data?.success ? toolsQ.data.data.platformTools : [];
  const live = toolsQ.data?.success ? toolsQ.data.data.live : null;

  const agentEventTypes = useMemo(() => eventTypes.filter(routedToAgent), [eventTypes, effective]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadIntoTester = (t: string) => {
    setSelectedType(t);
    setActorUsername("tester");
    setEntityId("");
    setMetaText(JSON.stringify(EXAMPLE_METADATA[t] ?? {}, null, 2));
    setResult(null);
  };

  const parseMeta = (): Record<string, unknown> | null => {
    const raw = metaText.trim();
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed !== "object" || Array.isArray(parsed)) {
        toast.error("metadata must be a JSON object");
        return null;
      }
      return parsed as Record<string, unknown>;
    } catch {
      toast.error("metadata is not valid JSON");
      return null;
    }
  };

  const buildInput = () => {
    const metadata = parseMeta();
    if (metadata === null) return null;
    return {
      type: selectedType,
      actorUsername: actorUsername.trim() || undefined,
      entityId: entityId.trim() || undefined,
      metadata,
    };
  };

  const handlePreview = async () => {
    const input = buildInput();
    if (!input) return;
    try {
      const res = await preview(input).unwrap();
      setResult(res.data);
      if (!res.data.handled) {
        toast.info(`No agent builder for "${selectedType}" — nothing would be sent.`);
      }
    } catch (e: unknown) {
      const msg = e && typeof e === "object" && "data" in e ? JSON.stringify((e as { data: unknown }).data) : String(e);
      toast.error(`Preview failed: ${msg}`);
    }
  };

  const handleFire = async () => {
    const input = buildInput();
    if (!input) return;
    const ok = await confirm({
      title: "Fire test event to the live agent?",
      message: `This sends a real "${selectedType}" event to the agent runtime — it will react to it.`,
      tone: "danger",
      confirmLabel: "Fire to agent",
    });
    if (!ok) return;
    try {
      const res = await fire(input).unwrap();
      if (res.data?.payload) setResult({ handled: true, payload: res.data.payload });
      toast.success(`Fired "${selectedType}" to the agent`);
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "data" in e
          ? ((e as { data?: { message?: string } }).data?.message ?? JSON.stringify((e as { data: unknown }).data))
          : String(e);
      toast.error(`Fire failed: ${msg}`);
    }
  };

  if (!isAdmin) return <Navigate to="/admin" replace />;
  if (!ready) {
    return (
      <PageContainer>
        <p>Loading session…</p>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <h1 style={{ margin: 0 }}>Sitebot / Agent inspector</h1>
        <Link to="/admin">← Admin home</Link>
        <Link to="/admin/events">Event dispatch →</Link>
      </div>
      <Hint>
        A full look at the agent's interface — straight from the backend so it never drifts.{" "}
        <strong>MCP tools</strong> are what the agent can do; <strong>events</strong> are what the
        platform sends to it. The tester runs the real <code>buildAgentMessage()</code> builder, so
        the preview is exactly what gets sent.
      </Hint>

      {/* ─── MCP TOOLS ─────────────────────────────── */}
      <Card>
        <h2 style={{ marginTop: 0 }}>
          MCP tools the agent has{" "}
          <span style={{ fontWeight: 400, color: "#777", fontSize: "0.9rem" }}>
            ({platformTools.length} platform tools)
          </span>
        </h2>
        <Hint>
          Defined in the backend (<code>services/agent/mcp/server.ts</code>) and exposed to the agent
          over MCP. The "live" panel reflects what the running agent actually sees.
        </Hint>
        {toolsQ.isLoading && <p>Loading tools…</p>}
        {toolsQ.error && (
          <p style={{ color: "#b91c1c" }}>
            Failed to load tools. <BasicButton text="Retry" onClick={() => void toolsQ.refetch()} />
          </p>
        )}
        {!!platformTools.length && (
          <Scroll>
            <Table>
              <thead>
                <tr>
                  <th>Tool</th>
                  <th>Description</th>
                  <th>Parameters</th>
                </tr>
              </thead>
              <tbody>
                {platformTools.map((t: McpToolInfo) => (
                  <tr key={t.name}>
                    <td>
                      <code style={{ whiteSpace: "nowrap" }}>{t.name}</code>
                    </td>
                    <td style={{ minWidth: 240, maxWidth: 420 }}>{t.description}</td>
                    <td>
                      <SchemaParams schema={t.inputSchema} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Scroll>
        )}

        <div style={{ marginTop: 16 }}>
          <h3 style={{ margin: "0 0 6px" }}>Live (as the agent sees it)</h3>
          {!live && <p style={{ color: "#777", fontSize: "0.85rem" }}>—</p>}
          {live?.error && (
            <p style={{ color: "#92400e", fontSize: "0.85rem" }}>
              ⚠️ Agent runtime offline / unreachable: {live.error}
            </p>
          )}
          {live && !live.error && (
            <div style={{ fontSize: "0.85rem" }}>
              <p style={{ margin: "0 0 6px" }}>
                Built-in agent tools: <strong>{live.builtinToolCount ?? "?"}</strong>
              </p>
              {(live.servers ?? []).map((s) => (
                <div key={s.name} style={{ marginBottom: 6 }}>
                  {s.connected ? badge("connected", "#dcfce7", "#166534") : badge("disconnected", "#fee2e2", "#991b1b")}{" "}
                  <code>{s.name}</code>{" "}
                  <span style={{ color: "#777" }}>
                    {s.url ?? "(no url)"} · {s.tools.length} tools
                  </span>
                </div>
              ))}
              {!(live.servers ?? []).length && <p style={{ color: "#777" }}>No MCP servers reported.</p>}
            </div>
          )}
        </div>
      </Card>

      {/* ─── EVENTS → AGENT ────────────────────────── */}
      <Card>
        <h2 style={{ marginTop: 0 }}>Events → agent</h2>
        <Hint>
          Every platform event type and whether it is routed to the agent sink (configured in{" "}
          <Link to="/admin/events">Event dispatch</Link>). "Test" loads the event into the tester
          below.
        </Hint>
        {dispatchQ.isLoading && <p>Loading events…</p>}
        {(dispatchQ.error || (!dispatchQ.isLoading && !dispatch)) && (
          <p style={{ color: "#b91c1c" }}>
            Failed to load events. <BasicButton text="Retry" onClick={() => void dispatchQ.refetch()} />
          </p>
        )}
        {!!eventTypes.length && (
          <Scroll>
            <Table>
              <thead>
                <tr>
                  <th>Event type</th>
                  <th>To agent?</th>
                  <th>All sinks</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {eventTypes.map((t) => (
                  <tr key={t} style={routedToAgent(t) ? { background: "#f0f9ff" } : undefined}>
                    <td>
                      <code style={{ whiteSpace: "nowrap" }}>{t}</code>
                    </td>
                    <td>{routedToAgent(t) ? badge("agent", "#dbeafe", "#1e40af") : <span style={{ color: "#bbb" }}>—</span>}</td>
                    <td style={{ color: "#555" }}>{(effective[t] ?? []).join(", ") || "—"}</td>
                    <td>
                      <BasicButton text="Test" onClick={() => loadIntoTester(t)} bgColor="#e0e7ff" hoverBgColor="#c7d2fe" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Scroll>
        )}
        {!!agentEventTypes.length && (
          <p style={{ fontSize: "0.8rem", color: "#555", marginTop: 10 }}>
            {agentEventTypes.length} of {eventTypes.length} event types route to the agent.
          </p>
        )}
      </Card>

      {/* ─── EVENT TESTER ──────────────────────────── */}
      <Card>
        <h2 style={{ marginTop: 0 }}>Event tester</h2>
        <Hint>
          Build a synthetic event and run it through the real backend builder. <strong>Preview</strong>{" "}
          shows the exact payload without sending; <strong>Fire to agent</strong> actually sends it to
          the live agent.
        </Hint>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}
        >
          <Field>
            Event type
            <select value={selectedType} onChange={(e) => loadIntoTester(e.target.value)}>
              {eventTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                  {routedToAgent(t) ? "  ·  → agent" : ""}
                </option>
              ))}
            </select>
          </Field>
          <Field>
            actorUsername
            <input value={actorUsername} onChange={(e) => setActorUsername(e.target.value)} placeholder="tester" />
          </Field>
          <Field>
            entityId <span style={{ fontWeight: 400, color: "#888" }}>(optional — e.g. AOTD id)</span>
            <input value={entityId} onChange={(e) => setEntityId(e.target.value)} placeholder="" />
          </Field>
          <Field style={{ gridColumn: "1 / -1" }}>
            metadata (JSON)
            <textarea rows={8} value={metaText} onChange={(e) => setMetaText(e.target.value)} />
          </Field>
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <BasicButton text={previewing ? "Previewing…" : "Preview"} onClick={() => void handlePreview()} disabled={previewing} />
          <BasicButton
            text={firing ? "Firing…" : "Fire to agent"}
            onClick={() => void handleFire()}
            disabled={firing}
            bgColor="#fecaca"
            hoverBgColor="#fca5a5"
          />
        </div>

        {result && (
          <div style={{ marginTop: 12 }}>
            {!result.handled || !result.payload ? (
              <p style={{ color: "#92400e" }}>
                No agent builder for <code>{selectedType}</code> — this event type is not turned into
                an agent payload (nothing is sent).
              </p>
            ) : (
              <>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  {badge(`source: ${result.payload.source}`, "#e5e7eb", "#333")}
                  {result.payload.urgency && badge(`urgency: ${result.payload.urgency}`, "#fef9c3", "#854d0e")}
                  {!!result.payload.attachments?.length &&
                    badge(`${result.payload.attachments.length} attachment(s)`, "#e0e7ff", "#3730a3")}
                </div>
                <h4 style={{ margin: "10px 0 0" }}>content (sent to the agent)</h4>
                <Pre>{result.payload.content}</Pre>
                <h4 style={{ margin: "10px 0 0" }}>metadata</h4>
                <Pre>{JSON.stringify(result.payload.metadata ?? {}, null, 2)}</Pre>
              </>
            )}
          </div>
        )}
      </Card>
    </PageContainer>
  );
};

export default AgentInspectorAdmin;
